import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SeoRedirect } from "@/components/SeoRedirect";
import { CookieConsent } from "@/components/CookieConsent";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import Index from "./rebuild/Home";
import { NotFound } from "./rebuild/Information";
import { VERTICALS } from "@/data/verticals";
import { RETIRED_ROUTES } from "@/data/retiredRoutes";
import { ACQ_MANIFEST } from "@/data/acquisition/manifest";
import { AcqRoute } from "@/acquisition/routes";

const About = lazy(() => import('./rebuild/Information').then(m=>({default:m.About})));
const Alternatives = lazy(() => import('./rebuild/Comparisons').then(m=>({default:m.Comparisons})));
const AlternativeDetail = lazy(() => import('./rebuild/Comparisons').then(m=>({default:m.Comparison})));
const Industries = lazy(() => import('./rebuild/Industries').then(m=>({default:m.Industries})));
const VerticalPage = lazy(() => import('./rebuild/Industries').then(m=>({default:m.Industry})));
const Solutions = lazy(() => import('./rebuild/Products'));
const Pricing = lazy(() => import('./rebuild/Pricing'));
const Faq = lazy(() => import('./rebuild/Information').then(m=>({default:m.Faq})));
const Verifications = lazy(() => import('./rebuild/Information').then(m=>({default:m.Verifications})));
const WhyUs = lazy(() => import('./rebuild/Information').then(m=>({default:m.WhyUs})));
const PrivacyPolicy = lazy(() => import('./rebuild/Privacy'));
const TermsOfService = lazy(() => import('./rebuild/Terms'));
const CookiePolicy = lazy(() => import('./rebuild/Cookies'));
const SecurityPage = lazy(() => import('./rebuild/Security'));
const AccessibilityPage = lazy(() => import('./rebuild/Accessibility'));
const Booking = lazy(() => import('./rebuild/Contact').then(m=>({default:m.Booking})));
const Contact = lazy(() => import('./rebuild/Contact').then(m=>({default:m.Contact})));
// Production's owned AEO service page and guide keep their existing route-level
// code splitting and data module (src/data/aeoProgram.ts); the rebuild's fork
// predates both, so they are restored here rather than ported into src/rebuild.
const Aeo = lazy(() => import("./pages/Aeo"));
const TampaPage = lazy(() => import("./pages/TampaPage"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const AiSearchVisibility = lazy(() => import("./pages/guides/AiSearchVisibility"));
const AiReceptionistCost = lazy(() => import("./pages/guides/AiReceptionistCost"));
const Tampa = lazy(() => import("./pages/Tampa"));
const TampaWebDesign = lazy(() => import("./pages/TampaWebDesign"));
const HvacRoundup = lazy(() => import("./pages/HvacRoundup"));

export const SiteRoutes = () => (
  // Suspense catches a pending import, not a rejected one. The boundary is
  // outside it so a chunk that 404s after a deploy shows a way out instead of
  // unmounting the tree and leaving the visitor on a blank page.
  <RouteErrorBoundary>
      <Suspense fallback={<div className="rb-loading" role="status">Loading Chleb AI…</div>}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/book" element={<Booking />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/comparisons" element={<Alternatives />} />
          <Route path="/alternatives" element={<Navigate to="/comparisons" replace />} />
          <Route path="/alternatives/:slug" element={<AlternativeDetail />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/faq" element={<Faq />} />
          {VERTICALS.map((v) => (
            <Route key={v.slug} path={v.route} element={<VerticalPage slug={v.slug} />} />
          ))}
          {/* The seven product routes became sections of /solutions on
              2026-09-07. The host serves a real 301 for each of them
              (public/_redirects, netlify.toml, vercel.json, all three read off
              the same src/data/retiredRoutes.ts by the build's SEO gate);
              these are the same redirects for anyone who reaches the SPA shell
              before a host rule can match. */}
          {RETIRED_ROUTES.map(({ from, to }) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/verifications" element={<Verifications />} />
          <Route path="/why-us" element={<WhyUs />} />
          <Route path="/aeo" element={<Aeo />} />
          <Route path="/guides/ai-search-visibility" element={<AiSearchVisibility />} />
          <Route path="/guides/ai-receptionist-cost" element={<AiReceptionistCost />} />
          <Route path="/tampa" element={<Tampa />} />
          <Route path="/tampa/:slug" element={<TampaPage />} />
          <Route path="/best/:slug" element={<TampaPage />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<TampaPage />} />
          <Route path="/tampa-web-design" element={<TampaWebDesign />} />
          <Route path="/compare/ai-receptionist-for-hvac-companies" element={<HvacRoundup />} />
          {/*
            Buyer tools, demos, guides and research (src/data/acquisition).
            The manifest carries paths only; each page's code and copy load
            with its own chunk.
          */}
          {ACQ_MANIFEST.map((r) => (
            <Route key={r.path} path={r.path} element={<AcqRoute key={r.path} path={r.path} component={r.component} />} />
          ))}

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
  </RouteErrorBoundary>
);

function LegacyHomeAnchor() {
 const {pathname,hash,search}=useLocation();
 const destinations:Record<string,string>={'#book':'/book','#contact':'/contact'};
 const to=pathname==='/'?destinations[hash]:pathname==='/crm'&&hash==='#book'?'/book':undefined;
 if(!to)return null;
 const [path,nextHash]=to.split('#');
 return <Navigate to={path+search+(nextHash?'#'+nextHash:'')} replace/>;
}
const App = () => (
  <TooltipProvider>
    {/* 2026-09-08: the two toast roots were removed. Nothing in the live app
        raises a toast (forms report inline), and mounting them pulled sonner
        and the toast store into the first script the phone has to evaluate. */}
    {/* First in the tree so its controls come first in keyboard order; it is
        position: fixed, so this changes nothing visually. */}
    <CookieConsent />
    <SeoRedirect />
    <BrowserRouter>
      <LegacyHomeAnchor/>
      <SiteRoutes/>
    </BrowserRouter>
  </TooltipProvider>
);

export default App;
